import { DSFlex } from '@/components/ds/Flex/Flex'
import { DSForm } from '@/components/ds/Form/Form'
import { DSLinkText } from '@/components/ds/LinkText/LinkText'
import { DSSpacer } from '@/components/ds/Spacer/Spacer'
import { DSText } from '@zoe/ds-web'
import { MinimalHeaderNonSticky } from '@/components/elements/header/MinimalHeaderNonSticky'
import { Container } from '@/components/shared/Container'
import { useAuth } from '@/lib/hooks/useAuth'
import Head from 'next/head'
import { useState } from 'react'
import * as Yup from 'yup'
import { EmailFormContent } from './EmailFormContent'

const PAGE_TITLE = 'ZOE - Reset Password'

const validationSchema = Yup.object({
  email: Yup.string().email('Email address does not look right').required('Email address is required'),
})

const initialValues = {
  email: '',
}

const PageHead: React.FC = () => {
  return (
    <Head>
      <title>{PAGE_TITLE}</title>
      <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      <meta name="robots" content="noindex,nofollow" />
    </Head>
  )
}

const TitleAndDescription: React.FC = () => {
  return (
    <>
      <DSText variant="fluid-heading-600" as="h2" align="center">
        Reset your password
      </DSText>
      <DSSpacer size={8} direction="vertical" />

      <DSText variant="fluid-paragraph-200" weight="light" align="center">
        Give us your email and we&apos;ll send you a link to reset your password.
      </DSText>
    </>
  )
}

const EmailSentSuccessMessage: React.FC = () => {
  return (
    <>
      <DSFlex direction="column" alignItems="center">
        <DSSpacer size={32} direction="vertical" />
        <DSText variant="fluid-paragraph-200" weight="light" align="center">
          We&apos;ve sent you an email with a link to reset your password. If you don&apos;t see it, check your spam
          folder or contact us at{' '}
          <DSLinkText href="mailto:hello@joinzoe.com" colourScheme="inherit" target="_blank">
            hello@joinzoe.com
          </DSLinkText>
          .
        </DSText>
      </DSFlex>
    </>
  )
}

const EmailSentErrorMessage: React.FC = () => {
  return (
    <>
      <DSSpacer size={32} direction="vertical" />
      <DSText variant="fluid-paragraph-200" color="error">
        Error sending reset password link. Please try again later or contact us at&nbsp;
        <DSLinkText href="mailto:hello@joinzoe.com" colourScheme="inherit" target="_blank" rel="noreferrer">
          hello@joinzoe.com
        </DSLinkText>
        .
      </DSText>
    </>
  )
}

export const ResetPasswordPage: React.FC = () => {
  const { sendPasswordResetEmail } = useAuth()
  const [sendEmailSuccess, setSendEmailSuccess] = useState<boolean | undefined>(undefined)

  const handleSubmitEmail = async (values: typeof initialValues) => {
    try {
      const sendResponse = await sendPasswordResetEmail(values.email)
      if (!sendResponse.error) {
        setSendEmailSuccess(true)
      } else {
        setSendEmailSuccess(false)
      }
    } catch (error) {
      setSendEmailSuccess(false)
    }
  }

  return (
    <Container $size="narrow">
      <PageHead />
      <MinimalHeaderNonSticky />
      {/* TODO: How can I make the above header blocking */}
      <DSSpacer size={32} direction="vertical" />

      <DSSpacer size={32} direction="vertical" />

      <DSFlex direction="column">
        <TitleAndDescription />
        <DSSpacer size={16} direction="vertical" />

        <DSFlex direction="row" alignItems="center" justifyContent="center">
          <DSFlex direction="column" width="100%">
            <DSForm
              initialValues={initialValues}
              onSubmit={handleSubmitEmail}
              validateOnChange={true}
              validationSchema={validationSchema}
            >
              <EmailFormContent />
            </DSForm>
          </DSFlex>
        </DSFlex>
      </DSFlex>

      {sendEmailSuccess === true && <EmailSentSuccessMessage />}
      {sendEmailSuccess === false && <EmailSentErrorMessage />}
    </Container>
  )
}
